import styles from './dynamic-area.scss';

import { customElement, property, query, queryAssignedNodes, state } from 'lit/decorators.js';
import { html, LitElement, PropertyValues } from 'lit';
import { when } from 'lit/directives/when.js';
import { localized, msg, str } from '@lit/localize';

import "../extras/usos-spinner";

type LoadingState = 'idle' | 'loading' | 'loaded' | 'soft-reloading';

/**
 * Dynamic area with an option to load content automatically when element becomes visible.
 *
 * @fires {Event} dynamic-area:urlLoaded - when dynamic-area loaded its dynamic contents.
 */
@customElement('dynamic-area')
@localized()
export class DynamicArea extends LitElement {
    static override styles = styles;

    /**
     * Whether the dialog content should be loaded automatically when element becomes visible.
     */
    @property({type: Boolean}) autoload = false;

    /**
     * Whether the dialog content should be reloaded automatically every time element becomes visible.
     */
    @property({type: Boolean}) autoreload = false;

    /**
     * If set to non-zero value, it forces soft reload every given number of seconds,
     * regardless of visibility.
     * Note that this property should not be used with autoload nor autoreload.
     */
    @property({type: Number, attribute: 'autorefresh'}) autorefreshInterval = 0;

    /**
     * Whether the content should be kept when element becomes invisible.
     * If false, the content will be cleared, and loaded again when element becomes visible again.
     * Note that this property affects only autoreload.
     */
    @property({type: Boolean, attribute: 'keep-content'}) keepContent = false;

    /**
     * Dynamic contents' URL.
     */
    @property() url = '';

    /**
     * aria-live property to be passed to loaded content container.
     */
    @property({attribute: 'arialive'}) arialive : 'off' | 'polite' | 'assertive'  = 'off';


    @state() private state: LoadingState = 'idle';

    @queryAssignedNodes() private _contentSlotAssignedList!: Node[];
    @query('#content') private _contentContainer!: HTMLElement;

    private static observer : IntersectionObserver = DynamicArea._createObserver();
    private _autorefreshTimer: Optional<ReturnType<typeof setInterval>> = null;

    override connectedCallback(): void {
        super.connectedCallback();
        this._startObserver();
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._stopObserver();
    }


    protected override updated(changedProperties: PropertyValues<this>): void {
        if (changedProperties.has('autoload') || changedProperties.has('autoreload')) {
            if (!this.autoload && !this.autoreload) this._stopObserver();
            else if (!this.autoreload && this.state === 'loaded') this._stopObserver();
            else this._startObserver();
        }
        if (changedProperties.has('autorefreshInterval')) {
            if (this.autorefreshInterval > 0) {
                if (this.autoload || this.autoreload) {
                    console.warn('dynamic-area: autorefreshInterval should not be used with autoload or autoreload, but both are set.');
                }
                this._startAutorefresh();
            } else {
                this._stopAutorefresh();
            }
        }
    }

    override render() {
        return html`
            <div id="content" aria-busy=${this.state === 'loading'} aria-live=${this.arialive}>
                ${when(this.state === 'loading', () => html`<div id="spinner"><usos-spinner></usos-spinner></div>`)}
                <slot></slot>
            </div>
        `;
    }


    /**
     * Manually reloads area.
     *
     * @param options - options for reloading.
     * @param options.soft - if true, the area won't be cleared before loading new content, and loading spinner won't be shown.
     *      Useful when you want to reload content without flickering, but it may have consequences if the new content
     *      is significantly different from the old one.
     * @returns Promise - resolved when area loaded correctly, rejected with
     * `${response.status}: ${response.statusText}` after fetch error.
     */
    async reload(options?: {soft?: boolean}): Promise<string> {
        return this._load(options?.soft ?? false);
    }

    // Managing IntersectionObserver

    private static _createObserver() : IntersectionObserver {
        const options = {
            root: null,
            threshold: 0.00,
        };
        return DynamicArea.observer ||
            new IntersectionObserver(DynamicArea._onIntersection, options);
    }

    private _startObserver() {
        if ((this.autoload && this.state !== 'loaded') || this.autoreload) {
            DynamicArea.observer.observe(this);
        }
    }

    private _stopObserver() {
        DynamicArea.observer.unobserve(this);
    }

    private _startAutorefresh() {
        this._stopAutorefresh();
        if (this.autorefreshInterval > 0) {
            this._autorefreshTimer = setInterval(() => {
                void this.reload({soft: true});
            }, this.autorefreshInterval * 1000);
        }
    }

    private _stopAutorefresh() {
        if (this._autorefreshTimer) {
            clearInterval(this._autorefreshTimer);
            this._autorefreshTimer = null;
        }
    }

    private static _onIntersection(entries: IntersectionObserverEntry[], _observer: IntersectionObserver) {
        entries.forEach(entry => {
            const target = entry.target as DynamicArea;
            if (entry.isIntersecting && (target.state !== 'loaded' || target.autoreload)) {
                void target.reload();
                if (!target.autoreload) target._stopObserver();
            }
            if (!entry.isIntersecting && !target.keepContent && target.autoreload) {
                target._clear();
                target.state = 'idle';
            }
        })
    }

    // Content loading

    private _clear(keepHeight = false) {
        this._contentContainer.style.minHeight = keepHeight ? `${this.offsetHeight}px` : '';
        this._contentSlotAssignedList.forEach(node => this.removeChild(node));
    }

    private async _load(soft: boolean): Promise<string> {
        if (this.state === 'loading' || this.state === 'soft-reloading')
            return '';
        if (!soft) {
            this._clear(this.autoreload);
        }
        this.state = soft ? 'soft-reloading' : 'loading';

        const response = await fetch(this.url);

        let content;
        if (response.ok) {
            content = await response.text();
        } else {
            content = `<div role="alert" style="color: var(--error);">${msg(str`Wystąpił błąd`)}</div>`;
        }

        // The cast is needed, as TypeScript assumes that state can be only 'loading' or 'soft-reloading' at this point,
        // but it can be 'idle' if the element became invisible during loading, and content was cleared.
        if (this.state as LoadingState === 'idle') {
            // If state is idle, it means that the element became invisible during loading, and content was cleared.
            // In this case, we shouldn't load the new content, but just stop loading state.
            this.dispatchEvent(
                new Event('dynamic-area:loadAbort', { composed: true, bubbles: true })
            );
            return '';
        }

        if (soft) {
            this._clear();
        }
        const d = document.createElement('div');
        d.innerHTML = content;
        this.appendChild(d);
        await DynamicArea._executeScriptElements(d);

        this.state = 'loaded';
        this._contentContainer.style.minHeight = '';

        this.dispatchEvent(
            new Event('dynamic-area:urlLoaded', { composed: true, bubbles: true })
        );

        if (response.ok) return '';
        else throw new Error(`${response.status}: ${response.statusText}`);
    }

    /* podmiana skryptów na tagi skrypt, żeby się odpaliły
     * https://stackoverflow.com/a/69190644 */
    private static async _executeScriptElements(containerElement: HTMLElement) {
        const scriptElements = containerElement.querySelectorAll("script");

        for (const scriptElement of Array.from(scriptElements)) {
            const clonedElement = document.createElement("script");

            Array.from(scriptElement.attributes).forEach((attribute) => {
                clonedElement.setAttribute(attribute.name, attribute.value);
            });

            clonedElement.text = scriptElement.text;
            const promise = new Promise((resolve, reject) => {
                if (clonedElement.hasAttribute('src')) {
                    clonedElement.addEventListener('load', resolve, { once: true });
                    clonedElement.addEventListener('error', reject, { once: true });
                } else {
                    resolve(null);
                }
            });

            scriptElement.parentNode!.replaceChild(clonedElement, scriptElement);
            await promise;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
      "dynamic-area": DynamicArea,
    }
    interface HTMLElementEventMap {
        'dynamic-area:urlLoaded': Event,
        'dynamic-area:loadAbort': Event,
    }
}
